import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { withStyles } from "@material-ui/core/styles";
import { Typography, WithStyles } from "@material-ui/core";
import { styles } from "./Contracts.styles";
import * as BusinessPartnerAction from "../../../../stores/businessPartners/BusinessPartnerAction";
import UniversalTable from "../../../components/UniversalTable/UniversalTable";
import { IUniversalTableColumn, IUniversalTableData } from "../../../components/UniversalTable/interfaces";
import { useSnackbar } from "notistack";
import ToastStatusEnum from "../../../../constants/ToastStatusEnum";
import { useTranslation } from "react-i18next";
import { selectRequesting } from "../../../../selectors/requesting/RequestingSelector";
import { Breadcrumbs } from "../../../components/Breadcrumbs/Breadcrumbs";
import { CenteredContainer } from "../../../components/CenteredContainer/CenteredContainer";
import BusinessPartnerModel from "../../../../models/businessPartner/BusinessPartnerModel";
import { CsvExportButton } from "../../../components/CsvExport/CsvExportButton";
import { today } from "../../../../utilities/dateTimeUtils";
import { BlueButton } from "../../../components/Buttons/BlueButton";
import { ImportBusinessPartnersDialog } from "./ImportBusinessPartnersDialog";
import { IBusinessPartner } from "../../../components/BusinessPartnersTextField/BusinessPartnersTextField";
import { selectAllCountries } from "../../../../selectors/countries/CountriesSelector";

export interface IBusinessPartnersViewProps extends WithStyles<typeof styles> {}

const BusinessPartnersViewPure: React.FunctionComponent<IBusinessPartnersViewProps> = ({
    classes
}: IBusinessPartnersViewProps) => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();

    const [isImportDialogShown, setIsImportDialogShown] = useState<boolean>(false);

    const allCountries = useSelector(state => selectAllCountries(state));

    const businessPartnersFromState = useSelector(state => state.businessPartners);
    const isRequesting = useSelector(state =>
        selectRequesting(state, [BusinessPartnerAction.REQUEST_ALL_BUSINESS_PARTNERS])
    );

    let businessPartners: IUniversalTableData;
    if (businessPartnersFromState) {
        businessPartners = Object.values(businessPartnersFromState)?.map((businessPartner: BusinessPartnerModel) => ({
            bpId: businessPartner.bpId,
            bpName: businessPartner.bpName,
            countryName: businessPartner.countryName,
            hasUser: businessPartner.hasUser,
            ...(businessPartner.hasUser && {
                actionsDisabled: ["edit", "delete"]
            })
        }));
    }

    const getCountryShortcut = (countryName: string): string =>
        allCountries?.find(c => c.name === countryName)?.shortcut;

    const handleEdit = (index, newData) => {
        const updatedBusinessPartner = {
            bpId: newData.bpId,
            bpName: newData.bpName,
            countryCode: getCountryShortcut(newData.countryName)
        };
        dispatch(BusinessPartnerAction.addOrEditBusinessPartner(businessPartners[index].bpId, updatedBusinessPartner))
            .then(() => {
                dispatch(BusinessPartnerAction.requestAllBusinessPartners());
                enqueueSnackbar("Business partner edited", {
                    variant: ToastStatusEnum.Info
                });
            })
            .catch(error =>
                enqueueSnackbar("Business partner editing failed: " + error?.message, {
                    variant: ToastStatusEnum.Error
                })
            );
    };

    const handleAdd = data => {
        const newBusinessPartner = {
            bpId: data.bpId,
            bpName: data.bpName,
            countryCode: getCountryShortcut(data.countryName)
        };
        dispatch(BusinessPartnerAction.addOrEditBusinessPartner(data.bpId, newBusinessPartner))
            .then(() => {
                dispatch(BusinessPartnerAction.requestAllBusinessPartners());
                enqueueSnackbar("Business partner added", {
                    variant: ToastStatusEnum.Info
                });
            })
            .catch(error =>
                enqueueSnackbar("Business partner adding failed: " + error?.message, {
                    variant: ToastStatusEnum.Error
                })
            );
    };
    const handleDelete = index => {
        dispatch(BusinessPartnerAction.deleteBusinessPartner(businessPartners[index].bpId))
            .then(() => {
                dispatch(BusinessPartnerAction.requestAllBusinessPartners());
                enqueueSnackbar("Business partner deleted", {
                    variant: ToastStatusEnum.Info
                });
            })
            .catch(error =>
                enqueueSnackbar("Business partner deletion failed: " + error?.message, {
                    variant: ToastStatusEnum.Error
                })
            );
    };

    const handleImport = (businessPartners: IBusinessPartner[]) => {
        setIsImportDialogShown(false);
        dispatch(BusinessPartnerAction.importBusinessPartners(businessPartners))
            .then(() => {
                dispatch(BusinessPartnerAction.requestAllBusinessPartners());
                enqueueSnackbar("Business partners imported", {
                    variant: ToastStatusEnum.Info
                });
            })
            .catch(error =>
                enqueueSnackbar("Business partner import failed: " + error?.message, {
                    variant: ToastStatusEnum.Error
                })
            );
    };

    const columns: IUniversalTableColumn[] = [
        {
            title: "BP ID",
            field: "bpId",
            type: "number",
            editable: "onAdd",
            validate: rowData => {
                if (rowData?.tableData?.editing) return true;

                const bpIdsToCheck = businessPartners?.map(bP => bP.bpId);
                const isAlreadyUsed = bpIdsToCheck?.findIndex(bpId => bpId === parseInt(rowData?.bpId)) !== -1;

                if (rowData?.bpId < 0) return { isValid: false, helperText: "BP ID must not be negative" };

                if (isAlreadyUsed) return { isValid: false, helperText: "This BP ID is already used." };

                if (rowData?.bpId === undefined || rowData?.bpId?.length === 0)
                    return { isValid: false, helperText: "A BP ID must not be empty" };

                return true;
            }
        },
        {
            title: "BP Name",
            field: "bpName",
            type: "text",
            validate: rowData => {
                if (rowData?.bpName === undefined || rowData?.bpName?.length === 0)
                    return { isValid: false, helperText: "A BP Name must not be empty" };
                return true;
            }
        },
        {
            type: "countries",
            title: "Country",
            field: "countryName",
            allCountries: allCountries.map(country => country.name),
            validate: rowData => {
                if (
                    rowData?.countryName === undefined ||
                    rowData?.countryName === null ||
                    rowData?.countryName?.length === 0
                )
                    return { isValid: false, helperText: "Country must not be empty" };
                return true;
            }
        },
        {
            type: "actions",
            title: t("admin.taskView.actions"),
            actions: [
                {
                    name: "edit",
                    tooltip: t("general.edit"),
                    onEditFinished: (index, newValue) => handleEdit(index, newValue),
                    scope: "businessPartners.write"
                },
                {
                    name: "add",
                    tooltip: "Add a business partner",
                    onAddFinished: newValue => handleAdd(newValue),
                    scope: "businessPartners.write"
                },
                {
                    name: "delete",
                    tooltip: "Delete",
                    onDeleteFinished: index => handleDelete(index),
                    deleteQuestion: "Do you want to delete this business partner?"
                }
            ]
        }
    ];

    const exportMapping = [
        {
            label: "BP ID",
            key: "bpId"
        },
        {
            label: "BP Name",
            key: "bpName"
        },
        {
            label: "Country",
            key: "countryName"
        }
    ];

    return (
        <CenteredContainer>
            <Breadcrumbs
                items={[
                    { title: t("admin.adminPanel"), href: "/admin" },
                    { title: "Contracts", href: "/admin/contracts" },
                    { title: "Manage Business partners" }
                ]}
            />
            <div className={classes.title}>
                <Typography variant="h4">Manage Business partners</Typography>
            </div>
            <div className={classes.exportButton}>
                <CsvExportButton
                    data={businessPartners || []}
                    outputFilename={`business_partners_${today().format("YYYYMMDD")}.csv`}
                    mappings={exportMapping}
                >
                    Export business partners
                </CsvExportButton>
                <BlueButton style={{ margin: 0 }} onClick={() => setIsImportDialogShown(true)}>
                    Import business partners
                </BlueButton>
            </div>
            <UniversalTable columns={columns} data={businessPartners} isLoading={isRequesting} searchable />
            {isImportDialogShown && (
                <ImportBusinessPartnersDialog onConfirm={handleImport} onClose={() => setIsImportDialogShown(false)} />
            )}
        </CenteredContainer>
    );
};

export const BusinessPartnersView = withStyles(styles)(BusinessPartnersViewPure);
